import { useState } from "react";
import { GraduationCap, ChevronRight, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * Dialog asking the user whether to remember the manual shelf-life overrides
 * they just made during a scan. Rendered between "click Save" and the actual
 * persistence call.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (boolean) => void
 *  - candidates: Array<{ row_id, name, category, ai_days, user_days, diff_days }>
 *  - onConfirm: (selected_candidates: same shape[]) => Promise — called when user picks "Salva e ricorda"
 *  - onSkip: () => Promise — called when user picks "Solo questa volta" (no learning)
 */
export default function LearnExpiryDialog({ open, onOpenChange, candidates = [], onConfirm, onSkip }) {
  const [selected, setSelected] = useState(() => new Set(candidates.map((c) => c.row_id)));
  const [busy, setBusy] = useState(false);

  // Reset selection when the dialog opens with new candidates
  if (open && candidates.length && selected.size === 0 && !busy) {
    setSelected(new Set(candidates.map((c) => c.row_id)));
  }

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSkip = async () => {
    setBusy(true);
    try { await onSkip?.(); }
    finally { setBusy(false); setSelected(new Set()); }
  };

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const picks = candidates.filter((c) => selected.has(c.row_id));
      await onConfirm?.(picks);
    } finally {
      setBusy(false);
      setSelected(new Set());
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent
        data-testid="learn-expiry-dialog"
        className="max-w-lg bg-white"
        onPointerDownOutside={(e) => busy && e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#E8F3EC] text-[#2B5A3F] flex items-center justify-center flex-shrink-0">
              <GraduationCap size={20} />
            </div>
            <div>
              <DialogTitle style={{ fontFamily: "Work Sans, sans-serif" }}>
                Vuoi che ricordi queste scadenze?
              </DialogTitle>
              <DialogDescription>
                Hai modificato {candidates.length} {candidates.length === 1 ? "scadenza suggerita" : "scadenze suggerite"}.
                Posso imparare le tue preferenze: dopo 3 conferme, future scansioni useranno il tuo valore al posto della stima generica.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-2 max-h-[40vh] overflow-y-auto">
          {candidates.map((c) => {
            const isPicked = selected.has(c.row_id);
            const direction = c.user_days < c.ai_days ? "−" : "+";
            const diffAbs = Math.abs(c.diff_days);
            return (
              <label
                key={c.row_id}
                data-testid={`learn-candidate-${c.row_id}`}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  isPicked ? "bg-[#E8F3EC] border-[#C5E1D1]" : "bg-white border-[#EAE7E1] hover:bg-[#F4F1ED]"
                }`}
              >
                <Checkbox
                  checked={isPicked}
                  onCheckedChange={() => toggle(c.row_id)}
                  data-testid={`learn-checkbox-${c.row_id}`}
                  className="flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-xs text-[#5C5F5A] flex items-center gap-1.5 flex-wrap mt-0.5">
                    <span className="font-mono">{c.ai_days}gg</span>
                    <ChevronRight size={12} className="text-[#8E918C]" />
                    <span className="font-mono font-semibold text-[#2B5A3F]">{c.user_days}gg</span>
                    <span className="text-[#8E918C]">({direction}{diffAbs}gg)</span>
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            data-testid="learn-skip-btn"
            disabled={busy}
            onClick={handleSkip}
            className="text-[#5C5F5A]"
          >
            <X size={14} className="mr-1.5" />
            Solo questa volta
          </Button>
          <Button
            type="button"
            data-testid="learn-confirm-btn"
            disabled={busy || selected.size === 0}
            onClick={handleConfirm}
            className="bg-[#2E4A3B] hover:bg-[#21382C] text-white"
          >
            <GraduationCap size={14} className="mr-1.5" />
            Salva e ricorda ({selected.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
